import { z } from 'zod';

import { type FilterOperator } from '@apps/web/components/ui/filter_bar';
import { getDateStringInTimezone } from '@apps/web/lib/date_utils';
import { type components } from '@libraries/typescript/api_client';

type ConversationType = components['schemas']['ConversationType'];
type AgentExecutionBackend = components['schemas']['AgentExecutionBackend'];
type QueryableConversationType = Exclude<ConversationType, 'admin_playground'>;
export type AgentTraceTypeFilterValue =
    | QueryableConversationType
    | 'automation'
    | 'swarm';

export const REVIEW_STATUS_VALUES = [
    'needs_review',
    'reviewed',
    'ticket_filed',
    'false_positive',
    'known_issue',
] as const;
export const REVIEW_VIEW_VALUES = [...REVIEW_STATUS_VALUES, 'all'] as const;
export const REVIEW_STATUS_LABELS: Record<
    (typeof REVIEW_STATUS_VALUES)[number],
    string
> = {
    needs_review: 'Needs review',
    reviewed: 'Reviewed',
    ticket_filed: 'Ticket filed',
    false_positive: 'False positive',
    known_issue: 'Known issue',
};
export const REVIEW_VIEW_LABELS: Record<
    (typeof REVIEW_VIEW_VALUES)[number],
    string
> = {
    needs_review: REVIEW_STATUS_LABELS.needs_review,
    reviewed: REVIEW_STATUS_LABELS.reviewed,
    ticket_filed: REVIEW_STATUS_LABELS.ticket_filed,
    false_positive: REVIEW_STATUS_LABELS.false_positive,
    known_issue: REVIEW_STATUS_LABELS.known_issue,
    all: 'All',
};
export const CONVERSATION_TYPE_VALUES = [
    'general',
    'outreach_analysis',
    'agent_trace_llm_judge',
    'internal',
    'automation',
    'swarm',
] as const satisfies ReadonlyArray<AgentTraceTypeFilterValue>;
export const CONVERSATION_TYPE_LABELS: Record<
    AgentTraceTypeFilterValue,
    string
> = {
    general: 'General',
    outreach_analysis: 'Outreach Analysis',
    agent_trace_llm_judge: 'LLM Judge',
    internal: 'Internal',
    automation: 'Automation',
    swarm: 'Swarm Outreach',
};
export const AUTO_DRAFTED_VALUES = ['yes', 'no'] as const;
export const URGENCY_FILTER_VALUES = ['urgent_outreach'] as const;
export const EXECUTION_BACKEND_VALUES = [
    'event',
    'temporal',
    'legacy',
] as const satisfies ReadonlyArray<AgentExecutionBackend>;
export const RUN_CHANNEL_FILTER_VALUES = [
    'web',
    'imessage',
    'slack',
    'unspecified',
] as const;
export const TRACE_SOURCE_VALUES = [
    'slack',
    'linq_imessage',
    'linq_imessage_subagent',
    'admin',
    'extension',
    'training',
    'v3',
] as const;
export const REVIEW_SOURCE_VALUES = ['human', 'judge', 'auto_tagger'] as const;
export const REVIEW_SOURCE_LABELS: Record<
    (typeof REVIEW_SOURCE_VALUES)[number],
    string
> = {
    human: 'Human',
    judge: 'LLM judge',
    auto_tagger: 'Auto-tagger',
};
export const HUMAN_REVIEW_STATUS_VALUES = ['reviewed', 'not_reviewed'] as const;
export const HUMAN_REVIEW_STATUS_LABELS: Record<
    (typeof HUMAN_REVIEW_STATUS_VALUES)[number],
    string
> = {
    reviewed: 'Reviewed',
    not_reviewed: 'Not Reviewed',
};
export const LLM_JUDGE_STATUS_VALUES = ['reviewed', 'not_reviewed'] as const;
export const LLM_JUDGE_STATUS_LABELS: Record<
    (typeof LLM_JUDGE_STATUS_VALUES)[number],
    string
> = {
    reviewed: 'Reviewed',
    not_reviewed: 'Not reviewed',
};
export const FEEDBACK_LABEL_VALUES = ['good', 'bad'] as const;
export const FEEDBACK_LABELS: Record<
    (typeof FEEDBACK_LABEL_VALUES)[number],
    string
> = {
    good: 'Thumbs up',
    bad: 'Thumbs down',
};
// Keep these controlled episode-judge values in sync with
// agent_trace_auto_taggers.tools / episode_judge. They are URL contracts as
// well as filter options, so old shared links remain parseable.
export const EPISODE_USE_CASE_VALUES = [
    'find_caregivers',
    'view_shifts',
    'run_outreach',
    'assign_schedule',
    'send_message',
    'reporting_analytics',
    'data_lookup',
    'update_records',
    'configure_behavior',
    'product_help',
    'fyi_status',
    'other',
    'none',
] as const;
export const EPISODE_OUTCOME_VALUES = [
    'done',
    'done_with_issues',
    'partial',
    'unresolved',
    'declined_unsupported',
    'gave_up',
    'false_success',
    'user_canceled',
    'no_task',
    'none',
] as const;
export const EPISODE_AGENT_BEHAVIOR_VALUES = [
    'acceptable',
    'failure',
    'unclear',
] as const;
/** Snake-case episode values (use cases, behaviors) render with spaces. */
export function formatEpisodeFilterValue(value: string): string {
    return value.replace(/_/g, ' ');
}
export const EPISODE_OUTCOME_LABELS: Record<
    (typeof EPISODE_OUTCOME_VALUES)[number],
    string
> = {
    done: 'Done cleanly',
    done_with_issues: 'Done, with friction',
    partial: 'Partially done',
    unresolved: 'Left unresolved',
    declined_unsupported: 'Declined — can’t do it',
    gave_up: 'Gave up (was possible)',
    false_success: 'Claimed done — wasn’t',
    user_canceled: 'User canceled',
    no_task: 'Nothing to do',
    none: 'None',
};
/** Label an episode outcome, tolerating values outside the known enum. */
export function episodeOutcomeLabel(outcome: string): string {
    const known = EPISODE_OUTCOME_LABELS as Record<string, string>;
    return known[outcome] ?? formatEpisodeFilterValue(outcome);
}
// Prompt-version filter options are no longer a static list here: they are
// fetched at runtime from GET /admin/prompt-rollout/version-filter-options
// (stamp key × discovered versions) via usePromptVersionFilterOptions, so a new
// version file or skill surfaces automatically with no hand-edited list.

export const SORT_BY_VALUES = [
    'created_at',
    'total_cost_usd',
    'duration_ms',
    'first_response_ready_latency_ms',
    'judge_flag_count',
    'judge_label',
    'swarm',
] as const;

/**
 * Sentinel owner-filter values. A real owner filter carries an admin user id;
 * these two stand in for the "owned by me" and "unassigned" shortcuts so the
 * whole owner dimension rides on a single `owner` search param.
 */
export const OWNER_FILTER_MINE = '@me';
export const OWNER_FILTER_UNASSIGNED = '@unassigned';

/** The owner-dimension query params the list endpoint accepts. */
export type OwnerListParams = {
    readonly owner_user_id: string | undefined;
    readonly owned_by_me: boolean | undefined;
    readonly unassigned_only: boolean | undefined;
};

/**
 * Expand the single `owner` search param into the list endpoint's three owner
 * query params: the two sentinels map to the `owned_by_me` / `unassigned_only`
 * flags, and any other value is an explicit owner user id.
 */
export function ownerSearchToListParams(
    owner: string | undefined,
): OwnerListParams {
    return {
        owner_user_id:
            owner !== undefined &&
            owner !== OWNER_FILTER_MINE &&
            owner !== OWNER_FILTER_UNASSIGNED
                ? owner
                : undefined,
        owned_by_me: owner === OWNER_FILTER_MINE ? true : undefined,
        unassigned_only: owner === OWNER_FILTER_UNASSIGNED ? true : undefined,
    };
}

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

const reviewViewParam = z
    .preprocess(
        (value) => (value === 'unreviewed' ? 'needs_review' : value),
        z.enum(REVIEW_VIEW_VALUES),
    )
    .optional()
    .catch(undefined);

/**
 * Default `created_after` bound: midnight (local time) 7 days ago, as a UTC
 * ISO instant. The trace list applies this when the URL has neither an
 * absolute date range nor an explicit rolling window, so a fresh visit lands
 * on the last week instead of the whole history.
 */
export const DEFAULT_CREATED_AFTER_WINDOW_DAYS = 7;
export const DEFAULT_AGENT_TRACE_SORT_BY = 'created_at';
export const DEFAULT_AGENT_TRACE_SORT_ORDER = 'desc';

export function defaultCreatedAfter(referenceDate: Date = new Date()): string {
    const date = new Date(referenceDate);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - DEFAULT_CREATED_AFTER_WINDOW_DAYS);
    return date.toISOString();
}

/** Returns the exact instant at the start of a rolling day window. */
export function rollingWindowCreatedAfter(
    windowDays: number,
    referenceDate: Date = new Date(),
): string {
    return new Date(
        referenceDate.getTime() - windowDays * 24 * 60 * 60 * 1000,
    ).toISOString();
}

export type AgentTraceDatePickerValue = {
    readonly from: string;
    readonly to: string;
};

export function agentTraceDatePickerValue(
    search: Pick<
        AgentTracesSearch,
        'created_after' | 'created_before' | 'window'
    >,
    timezone: string,
    referenceDate: Date = new Date(),
): AgentTraceDatePickerValue | undefined {
    if (
        search.created_after === undefined &&
        search.created_before === undefined
    ) {
        if (search.window === 'all') {
            return undefined;
        }
        const createdAfter =
            search.window === undefined
                ? defaultCreatedAfter(referenceDate)
                : rollingWindowCreatedAfter(search.window, referenceDate);
        return {
            from: getDateStringInTimezone(new Date(createdAfter), timezone),
            to: getDateStringInTimezone(referenceDate, timezone),
        };
    }
    return {
        from: getDateStringInTimezone(
            new Date(
                search.created_after ?? search.created_before ?? referenceDate,
            ),
            timezone,
        ),
        to: getDateStringInTimezone(
            new Date(
                search.created_before ?? search.created_after ?? referenceDate,
            ),
            timezone,
        ),
    };
}

export const agentTracesSearchSchema = z.object({
    view: reviewViewParam,
    page: z.coerce.number().optional().default(1),
    organization_ids: arrayParam(z.string()),
    exclude_organization_ids: arrayParam(z.string()),
    use_cases: arrayParam(z.enum(EPISODE_USE_CASE_VALUES)),
    exclude_use_cases: arrayParam(z.enum(EPISODE_USE_CASE_VALUES)),
    all_use_cases: arrayParam(z.enum(EPISODE_USE_CASE_VALUES)),
    outcomes: arrayParam(z.enum(EPISODE_OUTCOME_VALUES)),
    exclude_outcomes: arrayParam(z.enum(EPISODE_OUTCOME_VALUES)),
    all_outcomes: arrayParam(z.enum(EPISODE_OUTCOME_VALUES)),
    agent_behaviors: arrayParam(z.enum(EPISODE_AGENT_BEHAVIOR_VALUES)),
    exclude_agent_behaviors: arrayParam(z.enum(EPISODE_AGENT_BEHAVIOR_VALUES)),
    all_agent_behaviors: arrayParam(z.enum(EPISODE_AGENT_BEHAVIOR_VALUES)),
    created_by_users: arrayParam(z.string()),
    exclude_created_by_users: arrayParam(z.string()),
    // ISO instants (UTC) so shared links pin the same moment in every
    // timezone; the inputs render/edit them in the viewer's local time.
    // Absolute bounds take precedence over `window`; when all three are
    // absent, the list applies the default rolling 7-day bound.
    created_after: z.string().optional(),
    created_before: z.string().optional(),
    window: z
        .union([z.literal('all'), z.coerce.number().int().min(1).max(365)])
        .optional()
        .catch(undefined),
    owners: arrayParam(z.string()),
    exclude_owners: arrayParam(z.string()),
    judge_label: z.string().optional(),
    judge_labels: arrayParam(z.string()),
    exclude_judge_labels: arrayParam(z.string()),
    all_judge_labels: arrayParam(z.string()),
    conversation_type: z.enum(CONVERSATION_TYPE_VALUES).optional(),
    conversation_types: arrayParam(z.enum(CONVERSATION_TYPE_VALUES)),
    exclude_conversation_types: arrayParam(z.enum(CONVERSATION_TYPE_VALUES)),
    auto_drafted: z.enum(AUTO_DRAFTED_VALUES).optional(),
    auto_drafted_values: arrayParam(z.enum(AUTO_DRAFTED_VALUES)),
    exclude_auto_drafted_values: arrayParam(z.enum(AUTO_DRAFTED_VALUES)),
    urgency: z.enum(URGENCY_FILTER_VALUES).optional(),
    urgency_values: arrayParam(z.enum(URGENCY_FILTER_VALUES)),
    exclude_urgency_values: arrayParam(z.enum(URGENCY_FILTER_VALUES)),
    execution_backend: z.enum(EXECUTION_BACKEND_VALUES).optional(),
    execution_backends: arrayParam(z.enum(EXECUTION_BACKEND_VALUES)),
    exclude_execution_backends: arrayParam(z.enum(EXECUTION_BACKEND_VALUES)),
    run_channels: arrayParam(z.enum(RUN_CHANNEL_FILTER_VALUES)),
    exclude_run_channels: arrayParam(z.enum(RUN_CHANNEL_FILTER_VALUES)),
    source: z.enum(TRACE_SOURCE_VALUES).optional(),
    sources: arrayParam(z.enum(TRACE_SOURCE_VALUES)),
    exclude_sources: arrayParam(z.enum(TRACE_SOURCE_VALUES)),
    human_review_status: z.enum(HUMAN_REVIEW_STATUS_VALUES).optional(),
    human_review_statuses: arrayParam(z.enum(HUMAN_REVIEW_STATUS_VALUES)),
    exclude_human_review_statuses: arrayParam(
        z.enum(HUMAN_REVIEW_STATUS_VALUES),
    ),
    llm_judge_status: z.enum(LLM_JUDGE_STATUS_VALUES).optional(),
    llm_judge_statuses: arrayParam(z.enum(LLM_JUDGE_STATUS_VALUES)),
    exclude_llm_judge_statuses: arrayParam(z.enum(LLM_JUDGE_STATUS_VALUES)),
    review_source: z.enum(REVIEW_SOURCE_VALUES).optional(),
    exclude_review_source: z.enum(REVIEW_SOURCE_VALUES).optional(),
    auto_tag_label: z.string().optional(),
    auto_tag_labels: arrayParam(z.string()),
    exclude_auto_tag_labels: arrayParam(z.string()),
    all_auto_tag_labels: arrayParam(z.string()),
    feedback_label: z.enum(FEEDBACK_LABEL_VALUES).optional(),
    feedback_labels: arrayParam(z.enum(FEEDBACK_LABEL_VALUES)),
    exclude_feedback_labels: arrayParam(z.enum(FEEDBACK_LABEL_VALUES)),
    all_feedback_labels: arrayParam(z.enum(FEEDBACK_LABEL_VALUES)),
    prompt_version: z.string().optional(),
    prompt_versions: arrayParam(z.string()),
    exclude_prompt_versions: arrayParam(z.string()),
    all_prompt_versions: arrayParam(z.string()),
    owner: z.string().optional(),
    q: z.string().optional(),
    sort_by: z.enum(SORT_BY_VALUES).optional(),
    sort_judge_label: z.string().optional(),
    sort_order: z.enum(['asc', 'desc']).optional(),
});

export type AgentTracesSearch = z.infer<typeof agentTracesSearchSchema>;
export type AgentTraceSortBy = (typeof SORT_BY_VALUES)[number];
export type SortOrder = NonNullable<AgentTracesSearch['sort_order']>;

/**
 * The detail view inherits every list filter/sort/page param so it can rebuild
 * the exact list it was opened from. `from_list` is set only when navigating
 * from the list; its absence (a shared link, bookmark, or refresh without the
 * params) is the signal that there is no list context to navigate.
 * `from_analysis` marks traces opened from the Agent Analysis page, so the
 * back arrow returns there instead of the trace list.
 */
export const agentTraceDetailSearchSchema = agentTracesSearchSchema.extend({
    admin_agent_run_id: z.string().optional(),
    admin_agent_trace_id: z.string().optional(),
    feedback_item_id: z.string().uuid().optional().catch(undefined),
    from_list: z.boolean().optional(),
    from_analysis: z.boolean().optional(),
});

export type AgentTraceDetailSearch = z.infer<
    typeof agentTraceDetailSearchSchema
>;

export function getDetailAdminAgentRunIdForTrace(
    search: AgentTraceDetailSearch,
    conversationId: string,
): string | null {
    return search.admin_agent_trace_id === conversationId
        ? (search.admin_agent_run_id ?? null)
        : null;
}

export function detailSearchWithAdminAgentRun(
    search: AgentTraceDetailSearch,
    conversationId: string,
    runId: string | null,
): AgentTraceDetailSearch {
    return {
        ...search,
        admin_agent_run_id: runId ?? undefined,
        admin_agent_trace_id: runId ? conversationId : undefined,
    };
}

export function detailSearchForTraceNavigation(
    search: AgentTraceDetailSearch,
): AgentTraceDetailSearch {
    return {
        ...search,
        feedback_item_id: undefined,
    };
}

/** Strip detail-only state back down to list search params, for returning to
 * the list with the same filters intact. */
export function detailSearchToListSearch(
    search: AgentTraceDetailSearch,
): AgentTracesSearch {
    const {
        admin_agent_run_id: _adminAgentRunId,
        admin_agent_trace_id: _adminAgentTraceId,
        feedback_item_id: _feedbackItemId,
        from_list: _fromList,
        from_analysis: _fromAnalysis,
        ...listSearch
    } = search;
    return listSearch;
}

export type TraceFilterDimensionKey =
    | 'use_case'
    | 'outcome'
    | 'agent_behavior'
    | 'human_review_status'
    | 'llm_judge_status'
    | 'organization'
    | 'owner'
    | 'created_by'
    | 'conversation_type'
    | 'judge_label'
    | 'auto_drafted'
    | 'urgency'
    | 'swarm_outreach'
    | 'execution_backend'
    | 'run_channel'
    | 'source'
    | 'auto_tag_label'
    | 'feedback_label'
    | 'prompt_version';

export type TraceFilterChip = {
    readonly dimension: TraceFilterDimensionKey;
    readonly operator?: FilterOperator;
    readonly values: Array<string>;
};

/** The subset of search params owned by the filter bar. */
export type AgentTraceFilterParams = {
    readonly use_cases: AgentTracesSearch['use_cases'];
    readonly exclude_use_cases: AgentTracesSearch['exclude_use_cases'];
    readonly all_use_cases: AgentTracesSearch['all_use_cases'];
    readonly outcomes: AgentTracesSearch['outcomes'];
    readonly exclude_outcomes: AgentTracesSearch['exclude_outcomes'];
    readonly all_outcomes: AgentTracesSearch['all_outcomes'];
    readonly agent_behaviors: AgentTracesSearch['agent_behaviors'];
    readonly exclude_agent_behaviors: AgentTracesSearch['exclude_agent_behaviors'];
    readonly all_agent_behaviors: AgentTracesSearch['all_agent_behaviors'];
    readonly organization_ids: Array<string> | undefined;
    readonly exclude_organization_ids: Array<string> | undefined;
    readonly created_by_users: Array<string> | undefined;
    readonly exclude_created_by_users: Array<string> | undefined;
    readonly owner: string | undefined;
    readonly owners: Array<string> | undefined;
    readonly exclude_owners: Array<string> | undefined;
    readonly conversation_type: AgentTracesSearch['conversation_type'];
    readonly conversation_types:
        | Array<NonNullable<AgentTracesSearch['conversation_type']>>
        | undefined;
    readonly exclude_conversation_types:
        | Array<NonNullable<AgentTracesSearch['conversation_type']>>
        | undefined;
    readonly judge_label: string | undefined;
    readonly judge_labels: Array<string> | undefined;
    readonly exclude_judge_labels: Array<string> | undefined;
    readonly all_judge_labels: Array<string> | undefined;
    readonly auto_drafted: AgentTracesSearch['auto_drafted'];
    readonly auto_drafted_values:
        | Array<NonNullable<AgentTracesSearch['auto_drafted']>>
        | undefined;
    readonly exclude_auto_drafted_values:
        | Array<NonNullable<AgentTracesSearch['auto_drafted']>>
        | undefined;
    readonly urgency: AgentTracesSearch['urgency'];
    readonly urgency_values:
        | Array<NonNullable<AgentTracesSearch['urgency']>>
        | undefined;
    readonly exclude_urgency_values:
        | Array<NonNullable<AgentTracesSearch['urgency']>>
        | undefined;
    readonly execution_backend: AgentTracesSearch['execution_backend'];
    readonly execution_backends:
        | Array<NonNullable<AgentTracesSearch['execution_backend']>>
        | undefined;
    readonly exclude_execution_backends:
        | Array<NonNullable<AgentTracesSearch['execution_backend']>>
        | undefined;
    readonly run_channels: AgentTracesSearch['run_channels'];
    readonly exclude_run_channels: AgentTracesSearch['exclude_run_channels'];
    readonly source: AgentTracesSearch['source'];
    readonly sources:
        | Array<NonNullable<AgentTracesSearch['source']>>
        | undefined;
    readonly exclude_sources:
        | Array<NonNullable<AgentTracesSearch['source']>>
        | undefined;
    readonly human_review_status: AgentTracesSearch['human_review_status'];
    readonly human_review_statuses:
        | Array<NonNullable<AgentTracesSearch['human_review_status']>>
        | undefined;
    readonly exclude_human_review_statuses:
        | Array<NonNullable<AgentTracesSearch['human_review_status']>>
        | undefined;
    readonly llm_judge_status: AgentTracesSearch['llm_judge_status'];
    readonly llm_judge_statuses:
        | Array<NonNullable<AgentTracesSearch['llm_judge_status']>>
        | undefined;
    readonly exclude_llm_judge_statuses:
        | Array<NonNullable<AgentTracesSearch['llm_judge_status']>>
        | undefined;
    readonly review_source: AgentTracesSearch['review_source'];
    readonly exclude_review_source: AgentTracesSearch['exclude_review_source'];
    readonly auto_tag_label: string | undefined;
    readonly auto_tag_labels: Array<string> | undefined;
    readonly exclude_auto_tag_labels: Array<string> | undefined;
    readonly all_auto_tag_labels: Array<string> | undefined;
    readonly feedback_label: AgentTracesSearch['feedback_label'];
    readonly feedback_labels:
        | Array<NonNullable<AgentTracesSearch['feedback_label']>>
        | undefined;
    readonly exclude_feedback_labels:
        | Array<NonNullable<AgentTracesSearch['feedback_label']>>
        | undefined;
    readonly all_feedback_labels:
        | Array<NonNullable<AgentTracesSearch['feedback_label']>>
        | undefined;
    readonly prompt_version: string | undefined;
    readonly prompt_versions: Array<string> | undefined;
    readonly exclude_prompt_versions: Array<string> | undefined;
    readonly all_prompt_versions: Array<string> | undefined;
};

function legacyReviewSourceToStatus(
    search: AgentTracesSearch,
    source: 'human' | 'judge',
): AgentTracesSearch['human_review_status'] {
    if (search.review_source === source) {
        return 'reviewed';
    }
    if (search.exclude_review_source === source) {
        return 'not_reviewed';
    }
    return undefined;
}

function hasFilterValues<T extends string>(values: Array<T> | undefined) {
    return (values ?? []).some((value) => value.trim().length > 0);
}

function hasAnyFilterValues<T extends string>(
    includeValues: Array<T> | undefined,
    excludeValues: Array<T> | undefined,
) {
    return hasFilterValues(includeValues) || hasFilterValues(excludeValues);
}

function includeValuesWithLegacy<T extends string>(
    includeValues: Array<T> | undefined,
    legacyValue: T | undefined,
    hasPluralFilter: boolean,
): Array<T> | undefined {
    if (hasPluralFilter) {
        return hasFilterValues(includeValues) ? includeValues : undefined;
    }
    return legacyValue ? [legacyValue] : undefined;
}

/**
 * Maps URL search params to filter-bar chips. The order here is the stable
 * render order of chips in the bar.
 */
export function searchToFilterChips(
    search: AgentTracesSearch,
): Array<TraceFilterChip> {
    const chips: Array<TraceFilterChip> = [];
    const pushChip = (
        dimension: TraceFilterDimensionKey,
        values: Array<string> | undefined,
        operator: FilterOperator = 'is',
    ) => {
        const filteredValues = (values ?? [])
            .map((value) => value.trim())
            .filter(Boolean);
        if (filteredValues.length > 0) {
            chips.push({ dimension, operator, values: filteredValues });
        }
    };
    const hasHumanReviewPluralFilter = hasAnyFilterValues(
        search.human_review_statuses,
        search.exclude_human_review_statuses,
    );
    const hasLlmJudgePluralFilter = hasAnyFilterValues(
        search.llm_judge_statuses,
        search.exclude_llm_judge_statuses,
    );
    const hasHumanReviewModernFilter =
        search.human_review_status !== undefined || hasHumanReviewPluralFilter;
    const hasLlmJudgeModernFilter =
        search.llm_judge_status !== undefined || hasLlmJudgePluralFilter;
    const shouldUseLegacyReviewSource =
        !hasHumanReviewModernFilter && !hasLlmJudgeModernFilter;
    const humanReviewStatus = shouldUseLegacyReviewSource
        ? legacyReviewSourceToStatus(search, 'human')
        : search.human_review_status;
    const llmJudgeStatus = shouldUseLegacyReviewSource
        ? legacyReviewSourceToStatus(search, 'judge')
        : search.llm_judge_status;
    const hasOwnerFilter = hasAnyFilterValues(
        search.owners,
        search.exclude_owners,
    );
    const hasConversationTypeFilter = hasAnyFilterValues(
        search.conversation_types,
        search.exclude_conversation_types,
    );
    const hasJudgeLabelFilter = hasAnyFilterValues(
        search.judge_labels,
        search.exclude_judge_labels,
    );
    const hasAutoDraftedFilter = hasAnyFilterValues(
        search.auto_drafted_values,
        search.exclude_auto_drafted_values,
    );
    const hasUrgencyFilter = hasAnyFilterValues(
        search.urgency_values,
        search.exclude_urgency_values,
    );
    const hasExecutionBackendFilter = hasAnyFilterValues(
        search.execution_backends,
        search.exclude_execution_backends,
    );
    const hasSourceFilter = hasAnyFilterValues(
        search.sources,
        search.exclude_sources,
    );
    const hasAutoTagFilter = hasAnyFilterValues(
        search.auto_tag_labels,
        search.exclude_auto_tag_labels,
    );
    const hasFeedbackFilter = hasAnyFilterValues(
        search.feedback_labels,
        search.exclude_feedback_labels,
    );
    const hasPromptVersionFilter = hasAnyFilterValues(
        search.prompt_versions,
        search.exclude_prompt_versions,
    );
    pushChip('use_case', search.use_cases);
    pushChip('use_case', search.all_use_cases, 'is_all');
    pushChip('use_case', search.exclude_use_cases, 'is_not');
    pushChip('outcome', search.outcomes);
    pushChip('outcome', search.all_outcomes, 'is_all');
    pushChip('outcome', search.exclude_outcomes, 'is_not');
    pushChip('agent_behavior', search.agent_behaviors);
    pushChip('agent_behavior', search.all_agent_behaviors, 'is_all');
    pushChip('agent_behavior', search.exclude_agent_behaviors, 'is_not');
    pushChip(
        'human_review_status',
        includeValuesWithLegacy(
            search.human_review_statuses,
            humanReviewStatus,
            hasHumanReviewPluralFilter,
        ),
    );
    pushChip(
        'human_review_status',
        search.exclude_human_review_statuses,
        'is_not',
    );
    pushChip(
        'llm_judge_status',
        includeValuesWithLegacy(
            search.llm_judge_statuses,
            llmJudgeStatus,
            hasLlmJudgePluralFilter,
        ),
    );
    pushChip('llm_judge_status', search.exclude_llm_judge_statuses, 'is_not');
    pushChip('organization', search.organization_ids);
    pushChip('organization', search.exclude_organization_ids, 'is_not');
    pushChip('created_by', search.created_by_users);
    pushChip('created_by', search.exclude_created_by_users, 'is_not');
    pushChip(
        'owner',
        includeValuesWithLegacy(search.owners, search.owner, hasOwnerFilter),
    );
    pushChip('owner', search.exclude_owners, 'is_not');
    const includedConversationTypes = includeValuesWithLegacy(
        search.conversation_types,
        search.conversation_type,
        hasConversationTypeFilter,
    );
    pushChip(
        'conversation_type',
        includedConversationTypes?.filter((value) => value !== 'swarm'),
    );
    if (includedConversationTypes?.includes('swarm')) {
        pushChip('swarm_outreach', ['swarm']);
    }
    pushChip(
        'conversation_type',
        search.exclude_conversation_types?.filter((value) => value !== 'swarm'),
        'is_not',
    );
    if (search.exclude_conversation_types?.includes('swarm')) {
        pushChip('swarm_outreach', ['swarm'], 'is_not');
    }
    pushChip(
        'judge_label',
        includeValuesWithLegacy(
            search.judge_labels,
            search.judge_label,
            hasJudgeLabelFilter,
        ),
    );
    pushChip('judge_label', search.all_judge_labels, 'is_all');
    pushChip('judge_label', search.exclude_judge_labels, 'is_not');
    pushChip(
        'auto_drafted',
        includeValuesWithLegacy(
            search.auto_drafted_values,
            search.auto_drafted,
            hasAutoDraftedFilter,
        ),
    );
    pushChip('auto_drafted', search.exclude_auto_drafted_values, 'is_not');
    pushChip(
        'urgency',
        includeValuesWithLegacy(
            search.urgency_values,
            search.urgency,
            hasUrgencyFilter,
        ),
    );
    pushChip('urgency', search.exclude_urgency_values, 'is_not');
    pushChip(
        'execution_backend',
        includeValuesWithLegacy(
            search.execution_backends,
            search.execution_backend,
            hasExecutionBackendFilter,
        ),
    );
    pushChip('execution_backend', search.exclude_execution_backends, 'is_not');
    pushChip('run_channel', search.run_channels);
    pushChip('run_channel', search.exclude_run_channels, 'is_not');
    pushChip(
        'source',
        includeValuesWithLegacy(search.sources, search.source, hasSourceFilter),
    );
    pushChip('source', search.exclude_sources, 'is_not');
    pushChip(
        'auto_tag_label',
        includeValuesWithLegacy(
            search.auto_tag_labels,
            search.auto_tag_label,
            hasAutoTagFilter,
        ),
    );
    pushChip('auto_tag_label', search.all_auto_tag_labels, 'is_all');
    pushChip('auto_tag_label', search.exclude_auto_tag_labels, 'is_not');
    pushChip(
        'feedback_label',
        includeValuesWithLegacy(
            search.feedback_labels,
            search.feedback_label,
            hasFeedbackFilter,
        ),
    );
    pushChip('feedback_label', search.all_feedback_labels, 'is_all');
    pushChip('feedback_label', search.exclude_feedback_labels, 'is_not');
    pushChip(
        'prompt_version',
        includeValuesWithLegacy(
            search.prompt_versions,
            search.prompt_version,
            hasPromptVersionFilter,
        ),
    );
    pushChip('prompt_version', search.all_prompt_versions, 'is_all');
    pushChip('prompt_version', search.exclude_prompt_versions, 'is_not');
    return chips;
}

/**
 * Inverse of searchToFilterChips. Returns every filter-owned param so that
 * dimensions absent from the chips are explicitly cleared from the URL.
 */
export function filterChipsToSearchParams(
    chips: Array<TraceFilterChip>,
): AgentTraceFilterParams {
    const params: {
        -readonly [
            K in keyof AgentTraceFilterParams
        ]: AgentTraceFilterParams[K];
    } = {
        use_cases: undefined,
        exclude_use_cases: undefined,
        all_use_cases: undefined,
        outcomes: undefined,
        exclude_outcomes: undefined,
        all_outcomes: undefined,
        agent_behaviors: undefined,
        exclude_agent_behaviors: undefined,
        all_agent_behaviors: undefined,
        organization_ids: undefined,
        exclude_organization_ids: undefined,
        created_by_users: undefined,
        exclude_created_by_users: undefined,
        owner: undefined,
        owners: undefined,
        exclude_owners: undefined,
        conversation_type: undefined,
        conversation_types: undefined,
        exclude_conversation_types: undefined,
        judge_label: undefined,
        judge_labels: undefined,
        exclude_judge_labels: undefined,
        all_judge_labels: undefined,
        auto_drafted: undefined,
        auto_drafted_values: undefined,
        exclude_auto_drafted_values: undefined,
        urgency: undefined,
        urgency_values: undefined,
        exclude_urgency_values: undefined,
        execution_backend: undefined,
        execution_backends: undefined,
        exclude_execution_backends: undefined,
        run_channels: undefined,
        exclude_run_channels: undefined,
        source: undefined,
        sources: undefined,
        exclude_sources: undefined,
        human_review_status: undefined,
        human_review_statuses: undefined,
        exclude_human_review_statuses: undefined,
        llm_judge_status: undefined,
        llm_judge_statuses: undefined,
        exclude_llm_judge_statuses: undefined,
        review_source: undefined,
        exclude_review_source: undefined,
        auto_tag_label: undefined,
        auto_tag_labels: undefined,
        exclude_auto_tag_labels: undefined,
        all_auto_tag_labels: undefined,
        feedback_label: undefined,
        feedback_labels: undefined,
        exclude_feedback_labels: undefined,
        all_feedback_labels: undefined,
        prompt_version: undefined,
        prompt_versions: undefined,
        exclude_prompt_versions: undefined,
        all_prompt_versions: undefined,
    };
    const assignValues = <T extends string>(
        values: Array<string>,
        allowedValues: ReadonlyArray<T> | null,
    ): Array<T> | Array<string> | undefined => {
        const filteredValues = values
            .map((value) => value.trim())
            .filter(Boolean);
        if (allowedValues === null) {
            return filteredValues.length > 0 ? filteredValues : undefined;
        }
        const allowed = new Set<string>(allowedValues);
        const validValues = filteredValues.filter((value): value is T =>
            allowed.has(value),
        );
        return validValues.length > 0 ? validValues : undefined;
    };
    for (const chip of chips) {
        if (chip.values.length === 0) {
            continue;
        }
        const operator = chip.operator ?? 'is';
        switch (chip.dimension) {
            case 'use_case':
                if (operator === 'is_not') {
                    params.exclude_use_cases = assignValues(
                        chip.values,
                        EPISODE_USE_CASE_VALUES,
                    ) as AgentTraceFilterParams['exclude_use_cases'];
                } else if (operator === 'is_all') {
                    params.all_use_cases = assignValues(
                        chip.values,
                        EPISODE_USE_CASE_VALUES,
                    ) as AgentTraceFilterParams['all_use_cases'];
                } else {
                    params.use_cases = assignValues(
                        chip.values,
                        EPISODE_USE_CASE_VALUES,
                    ) as AgentTraceFilterParams['use_cases'];
                }
                break;
            case 'outcome':
                if (operator === 'is_not') {
                    params.exclude_outcomes = assignValues(
                        chip.values,
                        EPISODE_OUTCOME_VALUES,
                    ) as AgentTraceFilterParams['exclude_outcomes'];
                } else if (operator === 'is_all') {
                    params.all_outcomes = assignValues(
                        chip.values,
                        EPISODE_OUTCOME_VALUES,
                    ) as AgentTraceFilterParams['all_outcomes'];
                } else {
                    params.outcomes = assignValues(
                        chip.values,
                        EPISODE_OUTCOME_VALUES,
                    ) as AgentTraceFilterParams['outcomes'];
                }
                break;
            case 'agent_behavior':
                if (operator === 'is_not') {
                    params.exclude_agent_behaviors = assignValues(
                        chip.values,
                        EPISODE_AGENT_BEHAVIOR_VALUES,
                    ) as AgentTraceFilterParams['exclude_agent_behaviors'];
                } else if (operator === 'is_all') {
                    params.all_agent_behaviors = assignValues(
                        chip.values,
                        EPISODE_AGENT_BEHAVIOR_VALUES,
                    ) as AgentTraceFilterParams['all_agent_behaviors'];
                } else {
                    params.agent_behaviors = assignValues(
                        chip.values,
                        EPISODE_AGENT_BEHAVIOR_VALUES,
                    ) as AgentTraceFilterParams['agent_behaviors'];
                }
                break;
            case 'human_review_status':
                if (operator === 'is_not') {
                    params.exclude_human_review_statuses = assignValues(
                        chip.values,
                        HUMAN_REVIEW_STATUS_VALUES,
                    ) as AgentTraceFilterParams['exclude_human_review_statuses'];
                } else {
                    params.human_review_statuses = assignValues(
                        chip.values,
                        HUMAN_REVIEW_STATUS_VALUES,
                    ) as AgentTraceFilterParams['human_review_statuses'];
                }
                break;
            case 'llm_judge_status':
                if (operator === 'is_not') {
                    params.exclude_llm_judge_statuses = assignValues(
                        chip.values,
                        LLM_JUDGE_STATUS_VALUES,
                    ) as AgentTraceFilterParams['exclude_llm_judge_statuses'];
                } else {
                    params.llm_judge_statuses = assignValues(
                        chip.values,
                        LLM_JUDGE_STATUS_VALUES,
                    ) as AgentTraceFilterParams['llm_judge_statuses'];
                }
                break;
            case 'organization':
                if (operator === 'is_not') {
                    params.exclude_organization_ids = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else {
                    params.organization_ids = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                }
                break;
            case 'owner':
                if (operator === 'is_not') {
                    params.exclude_owners = assignValues(chip.values, null) as
                        | Array<string>
                        | undefined;
                } else {
                    params.owners = assignValues(chip.values, null) as
                        | Array<string>
                        | undefined;
                }
                break;
            case 'created_by':
                if (operator === 'is_not') {
                    params.exclude_created_by_users = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else {
                    params.created_by_users = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                }
                break;
            case 'conversation_type':
            case 'swarm_outreach': {
                const values =
                    chip.dimension === 'swarm_outreach'
                        ? chip.values.filter((value) => value === 'swarm')
                        : chip.values;
                const assignedValues = assignValues(
                    values,
                    CONVERSATION_TYPE_VALUES,
                ) as AgentTraceFilterParams['conversation_types'];
                if (assignedValues === undefined) {
                    break;
                }
                if (operator === 'is_not') {
                    params.exclude_conversation_types = [
                        ...(params.exclude_conversation_types ?? []),
                        ...assignedValues,
                    ];
                    break;
                }
                params.conversation_types = [
                    ...(params.conversation_types ?? []),
                    ...assignedValues,
                ];
                break;
            }
            case 'judge_label':
                if (operator === 'is_not') {
                    params.exclude_judge_labels = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else if (operator === 'is_all') {
                    params.all_judge_labels = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else {
                    params.judge_labels = assignValues(chip.values, null) as
                        | Array<string>
                        | undefined;
                }
                break;
            case 'auto_drafted':
                if (operator === 'is_not') {
                    params.exclude_auto_drafted_values = assignValues(
                        chip.values,
                        AUTO_DRAFTED_VALUES,
                    ) as AgentTraceFilterParams['exclude_auto_drafted_values'];
                } else {
                    params.auto_drafted_values = assignValues(
                        chip.values,
                        AUTO_DRAFTED_VALUES,
                    ) as AgentTraceFilterParams['auto_drafted_values'];
                }
                break;
            case 'urgency':
                if (operator === 'is_not') {
                    params.exclude_urgency_values = assignValues(
                        chip.values,
                        URGENCY_FILTER_VALUES,
                    ) as AgentTraceFilterParams['exclude_urgency_values'];
                } else {
                    params.urgency_values = assignValues(
                        chip.values,
                        URGENCY_FILTER_VALUES,
                    ) as AgentTraceFilterParams['urgency_values'];
                }
                break;
            case 'execution_backend':
                if (operator === 'is_not') {
                    params.exclude_execution_backends = assignValues(
                        chip.values,
                        EXECUTION_BACKEND_VALUES,
                    ) as AgentTraceFilterParams['exclude_execution_backends'];
                } else {
                    params.execution_backends = assignValues(
                        chip.values,
                        EXECUTION_BACKEND_VALUES,
                    ) as AgentTraceFilterParams['execution_backends'];
                }
                break;
            case 'run_channel':
                if (operator === 'is_not') {
                    params.exclude_run_channels = assignValues(
                        chip.values,
                        RUN_CHANNEL_FILTER_VALUES,
                    ) as AgentTraceFilterParams['exclude_run_channels'];
                } else {
                    params.run_channels = assignValues(
                        chip.values,
                        RUN_CHANNEL_FILTER_VALUES,
                    ) as AgentTraceFilterParams['run_channels'];
                }
                break;
            case 'source':
                if (operator === 'is_not') {
                    params.exclude_sources = assignValues(
                        chip.values,
                        TRACE_SOURCE_VALUES,
                    ) as AgentTraceFilterParams['exclude_sources'];
                } else {
                    params.sources = assignValues(
                        chip.values,
                        TRACE_SOURCE_VALUES,
                    ) as AgentTraceFilterParams['sources'];
                }
                break;
            case 'auto_tag_label':
                if (operator === 'is_not') {
                    params.exclude_auto_tag_labels = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else if (operator === 'is_all') {
                    params.all_auto_tag_labels = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else {
                    params.auto_tag_labels = assignValues(chip.values, null) as
                        | Array<string>
                        | undefined;
                }
                break;
            case 'feedback_label':
                if (operator === 'is_not') {
                    params.exclude_feedback_labels = assignValues(
                        chip.values,
                        FEEDBACK_LABEL_VALUES,
                    ) as AgentTraceFilterParams['exclude_feedback_labels'];
                } else if (operator === 'is_all') {
                    params.all_feedback_labels = assignValues(
                        chip.values,
                        FEEDBACK_LABEL_VALUES,
                    ) as AgentTraceFilterParams['all_feedback_labels'];
                } else {
                    params.feedback_labels = assignValues(
                        chip.values,
                        FEEDBACK_LABEL_VALUES,
                    ) as AgentTraceFilterParams['feedback_labels'];
                }
                break;
            case 'prompt_version':
                if (operator === 'is_not') {
                    params.exclude_prompt_versions = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else if (operator === 'is_all') {
                    params.all_prompt_versions = assignValues(
                        chip.values,
                        null,
                    ) as Array<string> | undefined;
                } else {
                    params.prompt_versions = assignValues(chip.values, null) as
                        | Array<string>
                        | undefined;
                }
                break;
            default: {
                const exhaustive: never = chip.dimension;
                throw new Error(`Unhandled filter dimension: ${exhaustive}`);
            }
        }
    }
    return params;
}
